import { useCallback } from 'react';
import { useTranslation } from '@packages/utils';
import {
  useGetUsersListQuery,
  useExportUserRoleMutation,
} from 'redux/apiSlice/userGrow.slice';
import { Table } from 'components/Base/Table';
import useTable from 'components/Base/Table/hooks/useTable';
import CardContent from 'components/Base/Layout/Content/CardContent';
import {
  ETableStoragePaths,
  TColumnsState,
} from 'components/Base/Table/Table.types';
import { TAdminsDataUI } from 'data/types/admin.types';
import useNavigateWithBackState from 'hooks/useNavigateWithBackState';
import { styled } from 'styled-components';
import ExportDropdown from 'components/Shared/ExportDropdown';
import { appLocalStorage, EStorageNames } from 'helpers/storage';
import {
  ENotificationType,
  useShowToastNotification,
} from 'components/Base/Notification';
import useGetTeamTableData from './UserList.data';

const COLUMN_KEY_TO_EXPORT_NAME: Record<string, string> = {
  firstName: 'firstName',
  lastName: 'lastName',
  email: 'email',
  phone: 'phone',
  elcPMstatus: 'elcPmStatus',
  siteCoordinatorStatus: 'siteCoordinatorStatus',
  'last-login': 'lastLoginDate',
};

const SORT_FIELD_MAP: Record<string, string> = {
  firstName: 'FIRST_NAME',
  lastName: 'LAST_NAME',
};

const STORAGE_KEY = ETableStoragePaths.TEAM_USERS;

const StyledTable = styled(Table)<{ dataSource?: TAdminsDataUI[] }>`
  .ant-table-content {
    max-height: calc(100vh - 290px) !important; // Adjust 300px as needed
    overflow-y: hidden;
  }
`;

const UserTab = () => {
  const { t } = useTranslation({
    keyPrefix: 'descriptions.admin-portal.team.list.table',
  });

  const { defaultColumns } = useGetTeamTableData();
  const { showToastNotification } = useShowToastNotification();

  const {
    page,
    pageSize,
    sortColumn,
    setPageSize,
    handleTableChange,
    selectedFilters,
    hasFilters,
    onFiltersReset,
    columns,
    selectedRows,
    setSelectedRows,
  } = useTable<TAdminsDataUI>({
    storagePath: ETableStoragePaths.TEAM_USERS,
    defaultColumns,
  });

  const { data, isFetching } = useGetUsersListQuery({
    page,
    pageSize,
    sortColumn,
    filters: selectedFilters,
  });

  const [exportUserRole, { isLoading: isExporting }] =
    useExportUserRoleMutation();

  const { goPeopleDetails } = useNavigateWithBackState();

  const navigateToPeopleDetails = (record: TAdminsDataUI) => {
    return {
      onClick: () => goPeopleDetails(record.adminId, t('TITLE')),
    };
  };

  const getVisibleExportColumns = useCallback(() => {
    const tableSettings = appLocalStorage.getItem(EStorageNames.tableSettings);
    const columnsState = tableSettings?.[STORAGE_KEY] as TColumnsState[];
    if (!columnsState) {
      return Object.values(COLUMN_KEY_TO_EXPORT_NAME);
    }
    return columnsState
      .filter(col => col.show)
      .map(col => COLUMN_KEY_TO_EXPORT_NAME[col.key as string])
      .filter(Boolean);
  }, []);

  const buildExportFilters = useCallback(
    (ids?: string[]) => {
      const filters: Record<string, unknown> = {};
      if (ids?.length) {
        filters.ids = ids;
      }
      if (selectedFilters?.firstName?.[0]) {
        filters.firstName = (selectedFilters.firstName[0] as string)
          .toString()
          .trim();
      }
      if (selectedFilters?.lastName?.[0]) {
        filters.lastName = (selectedFilters.lastName[0] as string)
          .toString()
          .trim();
      }
      if (selectedFilters?.email?.[0]) {
        filters.email = (selectedFilters.email[0] as string).toString().trim();
      }
      if (selectedFilters?.phone?.[0]) {
        filters.phone = (selectedFilters.phone[0] as string).toString().trim();
      }
      if (selectedFilters?.elcPMstatus?.length) {
        filters.elcPmStatus = selectedFilters.elcPMstatus.join(',');
      }
      if (selectedFilters?.siteCoordinatorStatus?.length) {
        filters.siteCoordinatorStatus =
          selectedFilters.siteCoordinatorStatus.join(',');
      }
      if (selectedFilters?.cafeCoordinatorStatus?.length) {
        filters.cafeCoordinatorStatus =
          selectedFilters.cafeCoordinatorStatus.join(',');
      }
      return filters;
    },
    [selectedFilters],
  );

  const buildExportSort = useCallback(() => {
    if (!sortColumn.sortField || !sortColumn.sortDirection) return undefined;
    const field = SORT_FIELD_MAP[sortColumn.sortField];
    if (!field) return undefined;
    return { field, direction: sortColumn.sortDirection };
  }, [sortColumn]);

  const handleExportAll = useCallback(async () => {
    try {
      const sort = buildExportSort();
      await exportUserRole({
        columns: getVisibleExportColumns(),
        filters: buildExportFilters(),
        ...(sort && { sort }),
      }).unwrap();
    } catch {
      showToastNotification({
        message: 'Export failed. Please try again.',
        type: ENotificationType.ERROR,
      });
    }
  }, [
    exportUserRole,
    getVisibleExportColumns,
    buildExportFilters,
    buildExportSort,
    showToastNotification,
  ]);

  const handleExportSelected = useCallback(async () => {
    const { selectedRowsKeys } = selectedRows;
    if (!selectedRowsKeys?.length) {
      showToastNotification({
        message: 'Please select at least one row to export',
        type: ENotificationType.ERROR,
      });
      return;
    }
    try {
      const ids = selectedRowsKeys.map(key => String(key));
      const sort = buildExportSort();
      await exportUserRole({
        columns: getVisibleExportColumns(),
        filters: buildExportFilters(ids),
        ...(sort && { sort }),
      }).unwrap();
    } catch {
      showToastNotification({
        message: 'Export failed. Please try again.',
        type: ENotificationType.ERROR,
      });
    }
  }, [
    selectedRows,
    exportUserRole,
    getVisibleExportColumns,
    buildExportFilters,
    buildExportSort,
    showToastNotification,
  ]);

  return (
    <CardContent style={{ padding: 0, margin: 0, overflow: 'hidden' }}>
      <StyledTable
        tableTitle={t('USERS')}
        hasFilters={hasFilters}
        onFiltersReset={onFiltersReset}
        rowKey={(record: TAdminsDataUI) => record.adminId}
        currentPage={page}
        columns={columns as any[] | undefined}
        dataSource={data?.content}
        rowClassName="pointer"
        onRow={navigateToPeopleDetails}
        loading={isFetching}
        pageSize={pageSize}
        setPageSize={setPageSize}
        totalDataCount={data?.totalElements}
        onChange={handleTableChange}
        storagePath={ETableStoragePaths.TEAM_USERS}
        additionalActions={[
          <ExportDropdown
            key="export-actions"
            onExportAll={handleExportAll}
            onExportSelected={handleExportSelected}
            isExporting={isExporting}
          />,
        ]}
        selectedRows={selectedRows}
        setSelectedRows={setSelectedRows as any}
      />
    </CardContent>
  );
};

export default UserTab;
